/**
 * React shim over the shared `<simpl-dev-context-banner>` Web
 * Component from `@simpl/dev-banners` (simpl-core/packages/dev-banners).
 *
 * Polls `/dev-context` on the local dev-server (see
 * `unified-dev-server.js`) every few seconds so a `git checkout` on
 * the host updates the banner without a browser refresh. The endpoint
 * reads HEAD live from the mounted parent `.git/`, so this is
 * genuinely "as live as your polling interval." In prod deployments
 * the endpoint doesn't exist and every fetch quietly 404s — the Web
 * Component treats empty attributes as "no dev context to display"
 * and self-hides, so this mount is safe to leave in place across
 * environments.
 */

import { useEffect, useState } from "react";

type DevContext = {
  repo: string;
  worktree: string;
  branch: string;
  /**
   * Live-discovered SIMPL apps (docker-inspected server-side).
   * Passed through to `<simpl-dev-context-banner>`'s `apps`
   * attribute as JSON so the app-switcher menu reflects what's
   * ACTUALLY running on this machine, not a hardcoded default.
   * Empty when docker isn't reachable — the component then falls
   * back to its own defaults.
   */
  apps: Array<{ name: string; url: string }>;
};

/** How often to re-probe `/dev-context`. Every 3s feels live to the
 *  eye and costs nothing (a few dozen bytes; the endpoint is a couple
 *  of `fs.readFileSync` calls with no async work). */
const POLL_INTERVAL_MS = 3000;

export function DevContextBanner(): JSX.Element | null {
  const [ctx, setCtx] = useState<DevContext | null>(null);

  useEffect(() => {
    // Only ever probe from a local origin. On a deployed host,
    // CloudFront's SPA-fallback would return `index.html` for the
    // missing route, which res.ok is true for and JSON.parse would
    // throw on — cheap to skip entirely by hostname.
    const host = window.location.hostname;
    const isLocal =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.endsWith(".localhost");
    if (!isLocal) return;

    let cancelled = false;

    // The nginx dev-proxy on :8088 only routes `/api/*` and specific
    // auth paths to the backend; anything else (including this
    // dev-affordance endpoint) falls through to the CRA frontend and
    // gets `index.html`. Hitting the dev-server directly on :3001
    // bypasses that — CORS is already set up on the dev-server for
    // the :8088 origin, so credentials-include works fine. The URL
    // is hardcoded because this component ONLY runs on localhost
    // (see the `isLocal` gate above); production callers never
    // reach this code path.
    const devServerUrl = `http://${window.location.hostname}:3001/dev-context`;

    const probe = async () => {
      try {
        const res = await fetch(devServerUrl, { credentials: "include" });
        if (!res.ok) return;
        const data = (await res.json()) as DevContext;
        if (cancelled) return;
        // Only re-set when a field actually changed — otherwise
        // React re-renders the shim every poll for no reason.
        // Apps list is compared by JSON-encoded shape; the docker
        // discovery yields a stably-sorted list, so identical
        // shapes on identical inputs is a safe cheap check.
        setCtx((prev) => {
          if (
            prev &&
            prev.repo === data.repo &&
            prev.worktree === data.worktree &&
            prev.branch === data.branch &&
            JSON.stringify(prev.apps) === JSON.stringify(data.apps)
          ) {
            return prev;
          }
          return data;
        });
      } catch {
        // dev-server not listening. Bar stays hidden.
      }
    };

    probe();
    const interval = window.setInterval(probe, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  // Render even before the first probe resolves — the Web Component
  // self-hides when both attributes are empty, so this is a no-op in
  // the browser until state lands.
  //
  // `apps` is serialized as JSON when non-empty so the Web Component
  // can parse it out of the attribute. When empty (docker not
  // reachable, or hasn't polled yet) we OMIT the attribute so the
  // component falls back to its own hardcoded default list; passing
  // `"[]"` would override the defaults with an empty menu.
  const appsAttr =
    ctx?.apps && ctx.apps.length > 0 ? JSON.stringify(ctx.apps) : undefined;
  return (
    <simpl-dev-context-banner
      repo={ctx?.repo ?? ""}
      worktree={ctx?.worktree ?? ""}
      branch={ctx?.branch ?? ""}
      apps={appsAttr}
    />
  );
}
